import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type HTMLAttributes,
} from 'react';
import { usePathname } from '@/lib/navigation';
import { cn } from '@/lib/utils';

const HIDDEN_PATH_PREFIXES = ['/coming-soon', '/admin'];

/** Dimensões do foguete (SVG aponta para baixo; exaustão no topo). */
const ROCKET_H = 40;
const ROCKET_W = 26;
/** Sobreposição do rastro com a zona dos motores. */
const TRAIL_OVERLAP = 12;
const TRAIL_W = 8;
/** Desloca o eixo um pouco à direita (px) para afastar dos cards. */
const AXIS_NUDGE_X = 4;
/** Revalidação periódica da altura do documento (imagens, lazy load) — sem RO no html. */
const LAYOUT_POLL_MS = 900;

const FIRE_TRAIL_DOWN =
  'linear-gradient(to top, rgba(255, 110, 25, 0.98) 0%, rgba(255, 150, 40, 0.8) 40%, rgba(255, 200, 60, 0.35) 72%, transparent 100%)';
const FIRE_TRAIL_UP =
  'linear-gradient(to bottom, rgba(255, 110, 25, 0.98) 0%, rgba(255, 150, 40, 0.8) 40%, rgba(255, 200, 60, 0.35) 72%, transparent 100%)';

export const rocketScrollbarHideNativeClass = cn(
  '[scrollbar-width:none]',
  '[-ms-overflow-style:none]',
  '[&::-webkit-scrollbar]:hidden'
);

type LayoutMetrics = {
  viewH: number;
  maxScroll: number;
};

type RocketTrackProps = {
  /** `fixed` = scrollbar da janela; `absolute` = dentro de um contentor. */
  position?: 'fixed' | 'absolute';
  className?: string;
  /** Só scrollY — deve ser barato (sem forçar layout). */
  getScrollY: () => number;
  /** viewH + maxScroll — pode medir layout; chamado em resize / poucas vezes. */
  measureLayout: () => LayoutMetrics;
  setScrollTop: (top: number) => void;
  /** Elemento que emite scroll (window → null). */
  scrollTarget: Window | HTMLElement | null;
  /** Extra: observar mudanças de tamanho (só contentores com overflow, nunca documentElement). */
  observeRoot?: Element | null;
  enabled?: boolean;
};

function RocketScrollbarTrack({
  position = 'fixed',
  className,
  getScrollY,
  measureLayout,
  setScrollTop,
  scrollTarget,
  observeRoot = null,
  enabled = true,
}: RocketTrackProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const rocketRef = useRef<HTMLImageElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const layoutRef = useRef<LayoutMetrics>({ viewH: 0, maxScroll: 0 });
  const lastScrollY = useRef(0);
  const lastDir = useRef<'up' | 'down'>('down');
  const lastThumbY = useRef(-1);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingTrail = useRef(false);
  const dragging = useRef(false);
  const dragOffsetY = useRef(0);
  const dragTrackTop = useRef(0);
  const seeded = useRef(false);
  const visibleRef = useRef(false);

  const refreshLayout = useCallback(() => {
    const next = measureLayout();
    const prev = layoutRef.current;
    if (prev.viewH === next.viewH && prev.maxScroll === next.maxScroll) {
      return false;
    }
    layoutRef.current = next;

    const container = containerRef.current;
    if (!container) return true;

    const { viewH, maxScroll } = next;
    const show = viewH > 0 && maxScroll > 0;
    if (show !== visibleRef.current) {
      visibleRef.current = show;
      container.style.opacity = show ? '1' : '0';
      container.style.pointerEvents = show ? '' : 'none';
    }

    if (position === 'fixed') {
      container.style.height = `${viewH}px`;
    }
    return true;
  }, [measureLayout, position]);

  const paint = useCallback(
    (withTrail: boolean) => {
      const thumb = thumbRef.current;
      const rocket = rocketRef.current;
      const trail = trailRef.current;
      if (!thumb || !rocket || !trail) return;

      const { viewH, maxScroll } = layoutRef.current;
      if (viewH <= 0 || maxScroll <= 0) return;

      const scrollY = getScrollY();
      const pct = Math.min(1, Math.max(0, scrollY / maxScroll));
      // Subpixel — evita «degraus» de 1px que parecem engasgos.
      const thumbY = pct * (viewH - ROCKET_H);

      if (Math.abs(thumbY - lastThumbY.current) > 0.05) {
        lastThumbY.current = thumbY;
        thumb.style.transform = `translate3d(calc(-50% + ${AXIS_NUDGE_X}px), ${thumbY}px, 0)`;
      }

      if (!seeded.current) {
        lastScrollY.current = scrollY;
        seeded.current = true;
      }

      const dy = scrollY - lastScrollY.current;
      lastScrollY.current = scrollY;

      if (dy !== 0) {
        const nextDir: 'up' | 'down' = dy > 0 ? 'down' : 'up';
        if (nextDir !== lastDir.current) {
          lastDir.current = nextDir;
          rocket.style.transform = nextDir === 'up' ? 'rotate(180deg)' : 'rotate(0deg)';
        }
      }

      if (withTrail && dy !== 0) {
        const speed = Math.min(Math.abs(dy), 100);
        const trailLen = Math.min(viewH * 0.28, 28 + speed * 2.8);
        const dir = lastDir.current;
        const rawTrailY =
          dir === 'down' ? thumbY - trailLen + TRAIL_OVERLAP : thumbY + ROCKET_H - TRAIL_OVERLAP;
        const clampedY = Math.max(0, Math.min(viewH - trailLen, rawTrailY));

        trail.style.height = `${trailLen}px`;
        trail.style.transform = `translate3d(calc(-50% + ${AXIS_NUDGE_X}px), ${clampedY}px, 0)`;
        trail.style.background = dir === 'down' ? FIRE_TRAIL_DOWN : FIRE_TRAIL_UP;
        trail.style.opacity = '1';

        if (fadeTimer.current) clearTimeout(fadeTimer.current);
        // No arrasto o rastro fica mais tempo; no scroll solto some rápido.
        fadeTimer.current = setTimeout(
          () => {
            trail.style.opacity = '0';
          },
          dragging.current ? 220 : 120
        );
      }
    },
    [getScrollY]
  );

  const schedulePaint = useCallback(
    (withTrail: boolean) => {
      if (withTrail) pendingTrail.current = true;
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const trail = pendingTrail.current;
        pendingTrail.current = false;
        paint(trail);
      });
    },
    [paint]
  );

  const scrollFromClientY = useCallback(
    (clientY: number) => {
      const { viewH, maxScroll } = layoutRef.current;
      const trackTravel = Math.max(1, viewH - ROCKET_H);
      const y = clientY - dragTrackTop.current - dragOffsetY.current;
      const pct = Math.min(1, Math.max(0, y / trackTravel));
      setScrollTop(pct * maxScroll);
      // Pintura imediata + rastro no arrasto.
      paint(true);
    },
    [paint, setScrollTop]
  );

  useEffect(() => {
    if (!enabled) return;

    const thumb = thumbRef.current;
    const track = trackRef.current;
    if (!thumb || !track) return;

    seeded.current = false;
    lastThumbY.current = -1;
    refreshLayout();
    paint(false);

    const onScroll = () => schedulePaint(true);
    const onResize = () => {
      refreshLayout();
      schedulePaint(false);
    };

    const onThumbPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragging.current = true;
      thumb.setPointerCapture(event.pointerId);
      const rect = thumb.getBoundingClientRect();
      dragOffsetY.current = event.clientY - rect.top;
      dragTrackTop.current = track.getBoundingClientRect().top;
      document.body.style.userSelect = 'none';
      thumb.style.cursor = 'grabbing';
    };

    const onTrackPointerDown = (event: PointerEvent) => {
      if (event.target === thumb || thumb.contains(event.target as Node)) return;
      event.preventDefault();
      refreshLayout();
      const trackRect = track.getBoundingClientRect();
      dragTrackTop.current = trackRect.top;
      const { viewH, maxScroll } = layoutRef.current;
      const trackTravel = Math.max(1, viewH - ROCKET_H);
      const pct = Math.min(
        1,
        Math.max(0, (event.clientY - trackRect.top - ROCKET_H / 2) / trackTravel)
      );
      setScrollTop(pct * maxScroll);
      dragging.current = true;
      dragOffsetY.current = ROCKET_H / 2;
      thumb.setPointerCapture(event.pointerId);
      document.body.style.userSelect = 'none';
      paint(true);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!dragging.current) return;
      event.preventDefault();
      scrollFromClientY(event.clientY);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!dragging.current) return;
      dragging.current = false;
      try {
        thumb.releasePointerCapture(event.pointerId);
      } catch {
        /* already released */
      }
      document.body.style.userSelect = '';
      thumb.style.cursor = 'grab';
    };

    const target = scrollTarget ?? window;
    target.addEventListener('scroll', onScroll, { passive: true } as AddEventListenerOptions);
    window.addEventListener('resize', onResize, { passive: true });
    thumb.addEventListener('pointerdown', onThumbPointerDown);
    track.addEventListener('pointerdown', onTrackPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    // NÃO observar documentElement: ResizeObserver no <html> dispara durante o scroll
    // (imagens, sticky, gutters) e força layout sync → foguete «anda e trava».
    let ro: ResizeObserver | null = null;
    if (scrollTarget instanceof HTMLElement || observeRoot) {
      ro = new ResizeObserver(() => {
        if (refreshLayout()) schedulePaint(false);
      });
      if (scrollTarget instanceof HTMLElement) ro.observe(scrollTarget);
      if (observeRoot && observeRoot !== document.documentElement && observeRoot !== document.body) {
        ro.observe(observeRoot);
      }
    }

    const pollId =
      position === 'fixed'
        ? window.setInterval(() => {
            if (refreshLayout()) schedulePaint(false);
          }, LAYOUT_POLL_MS)
        : null;

    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      thumb.removeEventListener('pointerdown', onThumbPointerDown);
      track.removeEventListener('pointerdown', onTrackPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      ro?.disconnect();
      if (pollId != null) window.clearInterval(pollId);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      document.body.style.userSelect = '';
    };
  }, [
    enabled,
    paint,
    schedulePaint,
    refreshLayout,
    scrollFromClientY,
    scrollTarget,
    observeRoot,
    setScrollTop,
    position,
  ]);

  if (!enabled) return null;

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={cn(
        'pointer-events-none z-[40] overflow-visible',
        position === 'fixed' ? 'fixed top-0' : 'absolute inset-y-0 right-0',
        className
      )}
      style={{
        right: position === 'fixed' ? 0 : undefined,
        width: position === 'absolute' ? ROCKET_W + 14 : ROCKET_W + 16,
        opacity: 0,
      }}
    >
      <div
        ref={trackRef}
        className="pointer-events-auto absolute inset-0 cursor-pointer overflow-visible"
      >
        <div className="relative h-full w-full overflow-visible">
          <div
            ref={trailRef}
            className="pointer-events-none absolute left-1/2 z-0 rounded-full will-change-transform"
            style={{
              width: TRAIL_W,
              opacity: 0,
              transition: 'opacity 480ms cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 0 12px 3px rgba(255, 120, 30, 0.4)',
            }}
          />
          <div
            ref={thumbRef}
            className="pointer-events-auto absolute left-1/2 z-10 flex cursor-grab items-center justify-center overflow-visible will-change-transform active:cursor-grabbing"
            style={{ width: ROCKET_W }}
          >
            <img
              ref={rocketRef}
              src="/icons/rocketscroll.svg"
              alt=""
              draggable={false}
              className="pointer-events-none block max-w-none select-none"
              style={{
                width: ROCKET_W,
                height: ROCKET_H,
                objectFit: 'contain',
                objectPosition: 'center',
                filter: 'drop-shadow(0 0 5px rgba(255, 120, 40, 0.5))',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Scrollbar foguete da janela (help center + portal). */
export default function CustomScrollbar() {
  const pathname = usePathname();
  const hidden = HIDDEN_PATH_PREFIXES.some((prefix) => pathname?.startsWith(prefix));

  const getScrollY = useCallback(
    () => window.scrollY || document.documentElement.scrollTop || 0,
    []
  );

  const measureLayout = useCallback((): LayoutMetrics => {
    const viewH = window.innerHeight;
    const docH = Math.max(
      document.documentElement.scrollHeight,
      document.body?.scrollHeight ?? 0
    );
    return { viewH, maxScroll: Math.max(0, docH - viewH) };
  }, []);

  const setScrollTop = useCallback((top: number) => {
    // Um só alvo — escrever em html + body dispara scroll duplicado / luta entre motores.
    const root = document.scrollingElement ?? document.documentElement;
    root.scrollTop = top;
  }, []);

  return (
    <RocketScrollbarTrack
      position="fixed"
      className="z-[9999]"
      getScrollY={getScrollY}
      measureLayout={measureLayout}
      setScrollTop={setScrollTop}
      scrollTarget={null}
      enabled={!hidden}
    />
  );
}

type RocketScrollAreaProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  /** Classes do viewport interno (padding, etc.). */
  viewportClassName?: string;
};

/**
 * Contentor com overflow e scrollbar foguete (modais, listas).
 * Passe `max-h-*` / flex no `className` externo.
 */
export function RocketScrollArea({
  className,
  viewportClassName,
  children,
  ...props
}: RocketScrollAreaProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  const setViewportRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    setScrollEl(node);
  }, []);

  const getScrollY = useCallback(() => scrollRef.current?.scrollTop ?? 0, []);

  const measureLayout = useCallback((): LayoutMetrics => {
    const el = scrollRef.current;
    if (!el) return { viewH: 0, maxScroll: 0 };
    return {
      viewH: el.clientHeight,
      maxScroll: Math.max(0, el.scrollHeight - el.clientHeight),
    };
  }, []);

  const setScrollTop = useCallback((top: number) => {
    const el = scrollRef.current;
    if (el) el.scrollTop = top;
  }, []);

  return (
    <div className={cn('relative min-h-0 overflow-hidden', className)}>
      <div
        ref={setViewportRef}
        className={cn(
          'h-full max-h-full overflow-x-hidden overflow-y-auto overscroll-contain',
          // Espaço para o foguete/rastro não cobrirem o conteúdo.
          'pr-8',
          rocketScrollbarHideNativeClass,
          viewportClassName
        )}
        {...props}
      >
        {children}
      </div>
      <RocketScrollbarTrack
        position="absolute"
        getScrollY={getScrollY}
        measureLayout={measureLayout}
        setScrollTop={setScrollTop}
        scrollTarget={scrollEl}
        observeRoot={scrollEl}
        enabled={Boolean(scrollEl)}
      />
    </div>
  );
}
